// @ts-nocheck
"use client"

import React, { useState, useRef } from "react"
import FileUpload from "./FileUpload"
import CaseStudyViewer from "./CaseStudyViewer"
import useApiCalls from "../hooks/useApiCalls"
import useJobPolling from "../hooks/useJobPolling"
import { JOB_STATUSES, DEFAULT_GENERATION_OPTIONS } from "../utils/constants"
import { validateFile, logger, jobSession } from "../utils/helpers"

const CaseStudyGenerator = () => {
  // Refs
  const fileUploadRef = useRef(null)

  // State management
  const [selectedFile, setSelectedFile] = useState(null)
  const [currentJobId, setCurrentJobId] = useState(null)
  const [caseStudy, setCaseStudy] = useState(null)
  const [originalCaseStudy, setOriginalCaseStudy] = useState(null)
  const [activeVersion, setActiveVersion] = useState("current") // 'current', 'original', or 'merge'
  const [showCheckStatus, setShowCheckStatus] = useState(false)
  const [revisionJobId, setRevisionJobId] = useState(null)
  const [baselineJobId, setBaselineJobId] = useState(null) // For subsequent edit operations
  const [generationOptions] = useState(DEFAULT_GENERATION_OPTIONS)

  // Custom hooks
  const {
    loading: apiLoading,
    generateCaseStudy,
    checkJobStatus,
    downloadCaseStudy,
    submitRevision,
    mergeSections,
    retryOperation,
    clearError,
  } = useApiCalls()

  const {
    status: jobStatus,
    progress,
    error: pollingError,
    result: pollingResult,
    jobType: pollingJobType,
    startPolling,
    resetPolling,
  } = useJobPolling(checkJobStatus)

  // Check for existing job session on component mount
  React.useEffect(() => {
    const existingJob = jobSession.get()
    const versionInfo = jobSession.getVersionInfo()
    
    // Check if we're coming back from preview page
    const urlParams = new URLSearchParams(window.location.search)
    const shouldRestore = urlParams.get('restore') === 'true'
    
    if (shouldRestore && urlParams.has('restore')) {
      // Remove the restore parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }

    // New: auto-open when ?job_id= is present (e.g., after questionnaire submit)
    const autoJobId = urlParams.get('job_id')
    if (autoJobId && !currentJobId) {
      try {
        // Save to session and start polling using existing flow
        jobSession.save(autoJobId, 'generation')
        setShowCheckStatus(false)
        setCurrentJobId(autoJobId)
        setBaselineJobId(autoJobId)
        startPolling(autoJobId)
        // Remove job_id from URL
        const cleanUrl = new URL(window.location.href)
        cleanUrl.searchParams.delete('job_id')
        window.history.replaceState({}, document.title, cleanUrl.pathname + cleanUrl.search)
        logger.log('Auto-opening job from URL job_id:', autoJobId)
      } catch (err) {
        logger.warn('Failed to auto-open job_id from URL:', err)
      }
    }

    if (existingJob && !currentJobId) {

      // Check if we have multiple jobs (revision workflow or merged studies)
      if (jobSession.hasVersions() || shouldRestore) {
        // Auto-restore versions without showing banner
        handleCheckStatus()
      } else {
        // Show banner for single job recovery
        setShowCheckStatus(true)
      }
      logger.log("Found existing job session:", {
        existingJob,
        versionInfo,
        hasMerged: jobSession.hasMergedStudy(),
        shouldRestore,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJobId])

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = reader.result.split(",")[1]
        resolve(base64)
      }
      reader.onerror = reject
    })
  }

  // File selection handler
  const handleFileSelect = (file) => {
    const validation = validateFile(file)

    if (!validation.isValid) {
      logger.error("File validation failed:", validation.errors)
      return
    }

    setSelectedFile(file)
    logger.log("File selected:", file.name)
  }

  // File upload and generation process
  const handleGenerate = async () => {
    if (!selectedFile) return

    try {
      logger.log("Starting generation process...")

      // Read file as base64
      const fileContent = await fileToBase64(selectedFile)

      // Send directly to generation endpoint
      const generationResult = await generateCaseStudy({
        file_content: fileContent,
        filename: selectedFile.name,
        ...generationOptions,
      })

      // Use job_id (not jobId) to match backend response
      const jobId = generationResult.job_id
      setCurrentJobId(jobId)
      setBaselineJobId(jobId) // Set initial baseline for edit operations

      // Save job to session for persistence across refreshes
      jobSession.save(jobId, "generation")
      logger.log("Generation started, job ID:", jobId)

      // Start polling for job status
      startPolling(jobId)
    } catch (error) {
      logger.error("Generation failed:", error)
    }
  }

  // Handle polling completion
  React.useEffect(() => {
    if (pollingResult && jobStatus === JOB_STATUSES.COMPLETED) {
      logger.log("Polling result received:", pollingResult)

      // Only update if this polling result is for the current job
      const pollingJobId = pollingResult.job_id || pollingResult.result?.job_id
      if (pollingJobId && pollingJobId !== currentJobId) {
        logger.log("Ignoring stale polling result for job:", pollingJobId, "current job:", currentJobId)
        return
      }

      setActiveVersion("current")

      // Extract case study data from result
      const caseStudyData = pollingResult.case_study || pollingResult.revised_case_study
      const markdown = pollingResult.markdown

      logger.log("Extracted data:", { caseStudyData, markdown, hasMarkdown: !!markdown })

      if (markdown) {
        const newCaseStudy = {
          content: markdown,
          data: caseStudyData, // Store the structured data for revisions
          metadata: {
            job_id: currentJobId,
            fileName: selectedFile?.name,
            processingTime: pollingResult.processingTime,
            wordCount: pollingResult.wordCount || markdown.split(/\s+/).length,
            fileKey: pollingResult.file_key,
          },
        }

        // If this is a revision job, ensure we show the revised version
        if (pollingJobType === "revision") {
          setActiveVersion("current") // Show the new revision by default
        }

        setCaseStudy(newCaseStudy)

        logger.log("Case study set successfully")
      } else {
        logger.error("No markdown content found in result")
      }
    }
  }, [pollingResult, jobStatus, currentJobId, selectedFile, pollingJobType])

  // Download handler
  const handleDownload = async () => {
    if (!currentJobId) return

    try {
      await downloadCaseStudy(currentJobId)
      logger.log("Download initiated")
    } catch (error) {
      logger.error("Download failed:", error)
    }
  }

  // Feedback/revision handler
  const handleFeedbackSubmit = async (feedbackData, explicitCaseStudyData = null) => {
    try {
      // Use provided case study data (from merge result) or fallback to current state
      const caseStudyDataToUse = explicitCaseStudyData || caseStudy?.data
      
      if (!caseStudyDataToUse) {
        throw new Error("Case study data not available for revision")
      }

      // Promote the in‑memory current (maybe merged) study to original
      setOriginalCaseStudy(caseStudy)
      const baselineType = jobSession.hasMergedStudy() ? "merge" : "generation"
      jobSession.saveOriginal(caseStudy.metadata.job_id, baselineType)

      // Fire off your revision API call using the correct case study data
      const parentJobId = caseStudy.metadata.job_id // Current case study being revised
      const revisionResult = await submitRevision(caseStudyDataToUse, feedbackData, parentJobId)
      const newRevisionJobId = revisionResult.job_id

      // Update session + state
      setCurrentJobId(newRevisionJobId)
      setRevisionJobId(newRevisionJobId)
      setBaselineJobId(newRevisionJobId) // Set baseline for subsequent edit operations
      jobSession.save(newRevisionJobId, "revision")

      // ***Only now*** clear any old polling and start the new one
      clearError()
      resetPolling()
      startPolling(newRevisionJobId)

      logger.log("Revision job submitted successfully", { newRevisionJobId })
    } catch (error) {
      logger.error("Revision submission failed:", error)
    }
  }

  // Retry handler
  const _handleRetry = () => {
    clearError()
    resetPolling()
    if (selectedFile) {
      retryOperation(handleGenerate)
    }
  }

  // Check status handler for existing jobs
  const handleCheckStatus = async () => {
    const existingJob = jobSession.get()
    const versionInfo = jobSession.getVersionInfo()
    const previousJob = jobSession.getPrevious()
    const mergedJob = jobSession.getMerged()

    if (!existingJob) {
      setShowCheckStatus(false)
      return
    }

    try {
      setCurrentJobId(existingJob.job_id)
      setShowCheckStatus(false)

      // Handle merged case study restoration
      if (versionInfo.hasMerged && mergedJob && existingJob.job_id === mergedJob.job_id) {
        // This is a merged case study - restore the previous version as "original"
        if (previousJob) {
          try {
            const previousResult = await checkJobStatus(previousJob.job_id)
            if (previousResult && previousResult.status === "completed" && previousResult.result) {
              const previousCaseStudyData = previousResult.result.case_study || previousResult.result.revised_case_study
              const previousMarkdown = previousResult.result.markdown

              if (previousMarkdown) {
                const restoredPrevious = {
                  content: previousMarkdown,
                  data: previousCaseStudyData,
                  metadata: {
                    job_id: previousJob.job_id,
                    fileName: selectedFile?.name || "Previous Version",
                    fileKey: previousResult.result.file_key,
                  },
                }
                setOriginalCaseStudy(restoredPrevious)
                logger.log("Previous version restored as original for merged case study")
              }
            }
          } catch (error) {
            logger.warn("Failed to restore previous version:", error)
          }
        }
      } else {
        // Standard revision workflow - restore original case study
        const originalJob = jobSession.getOriginal()
        const revisionJob = jobSession.getRevision()

        if (originalJob && originalJob.job_id !== existingJob.job_id) {
          try {
            const originalResult = await checkJobStatus(originalJob.job_id)
            if (originalResult && originalResult.status === "completed" && originalResult.result) {
              const originalCaseStudyData = originalResult.result.case_study || originalResult.result.revised_case_study
              const originalMarkdown = originalResult.result.markdown

              if (originalMarkdown) {
                const restoredOriginal = {
                  content: originalMarkdown,
                  data: originalCaseStudyData,
                  metadata: {
                    job_id: originalJob.job_id,
                    fileName: selectedFile?.name || "Restored",
                    fileKey: originalResult.result.file_key,
                  },
                }
                setOriginalCaseStudy(restoredOriginal)
                logger.log("Original case study restored from session")
              }
            }
          } catch (error) {
            logger.warn("Failed to restore original case study:", error)
          }
        }

        // Restore revision job ID if available
        if (revisionJob && revisionJob.job_id === existingJob.job_id) {
          setRevisionJobId(revisionJob.job_id)
          setBaselineJobId(revisionJob.job_id) // Restore baseline for edit operations
        } else {
          // For non-revision jobs, use the current job as baseline
          setBaselineJobId(existingJob.job_id)
        }
      }

      // Start polling for the current job
      startPolling(existingJob.job_id)
      logger.log("Resuming job status check:", existingJob, versionInfo)
    } catch (error) {
      logger.error("Failed to check job status:", error)
      // Clear invalid session
      jobSession.clear()
      setShowCheckStatus(false)
    }
  }

  // Start over handler
  const handleStartOver = () => {
    setSelectedFile(null)
    setCurrentJobId(null)
    setCaseStudy(null)
    setOriginalCaseStudy(null)
    setRevisionJobId(null)
    setBaselineJobId(null) // Clear baseline job ID
    setActiveVersion("current")
    resetPolling()
    clearError()

    // Clear job session
    jobSession.clear()
    setShowCheckStatus(false)

    logger.log("Reset to initial state")
  }

  // Handle merge sections result
  const handleMergeSections = async (jobId, sectionSelections, customContent = {}) => {
    try {
      // (A) Cancel any existing poll before doing the merge
      clearError()
      resetPolling()
      
      // (B) If this is the first custom edit (no originalCaseStudy yet), preserve current as original
      if (!originalCaseStudy && caseStudy) {
        setOriginalCaseStudy(caseStudy)
        logger.log("First custom edit detected - preserving current case study as original")
      }

      // (C) Fire the merge
      const result = await mergeSections(jobId, sectionSelections, customContent)

      if (result?.case_study) {
        const mergedJobId = result.job_id || currentJobId
        const mergedCaseStudy = {
          content: result.markdown,
          data: result.case_study,
          metadata: {
            job_id: mergedJobId,
            fileName: caseStudy?.metadata?.fileName || "Merged Case Study",
            processingTime: result.processingTime,
            wordCount: result.wordCount || (result.markdown?.split(/\s+/).length ?? 0),
            fileKey: result.file_key,
          },
        }

        // (C) Update session + React state
        const originalJobId = originalCaseStudy?.metadata?.job_id
        // Only set currentRevisionJobId for actual AI revision workflows (when revisionJobId exists)
        const currentRevisionJobId = revisionJobId ? caseStudy?.metadata?.job_id : null
        jobSession.saveMerged(mergedJobId, originalJobId, currentRevisionJobId)
        setCurrentJobId(mergedJobId)
        
        // **CRITICAL FIX**: Always set baselineJobId to merged job ID for subsequent edit operations
        setBaselineJobId(mergedJobId)
        
        // Treat the merged result as the new sole current/original version
        setOriginalCaseStudy(mergedCaseStudy)
        setRevisionJobId(null)
        jobSession.promoteMergedToBaseline()

        setCaseStudy(mergedCaseStudy)
        setActiveVersion("current")

        // (D) **Cancel any leftover polling** so it never overwrites your merge
        resetPolling()

        window.scrollTo({ top: 0, behavior: "smooth" })
        logger.log("Merge sections completed successfully", { originalJobId, revisionJobId, mergedJobId })
      }

      return result
    } catch (error) {
      logger.error("Merge sections failed:", error)
      throw error
    }
  }
  // Determine current status for UI
  const getCurrentStatus = () => {
    if (apiLoading) return JOB_STATUSES.UPLOADING
    return jobStatus
  }

  const _getCurrentError = () => {
    return pollingError
  }

  const isProcessing = getCurrentStatus() === JOB_STATUSES.PROCESSING || getCurrentStatus() === JOB_STATUSES.UPLOADING

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 relative">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 pt-8">
          {caseStudy ? "RISCPoint Case Study Preview" : "RISCPoint Case Study Generator"}
        </h1>
        <p className="text-gray-600">
          {!caseStudy && !currentJobId && !selectedFile 
            ? "Choose how you'd like to create your case study"
            : ""
          }
        </p>
      </div>

      {/* Two-Path Navigation - Show until case study is generated */}
      {!caseStudy && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Primary Path: Questionnaire Form */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6 flex flex-col">
          <div className="text-center flex-grow">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Fill Out Questionnaire</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Quick guided form with auto-save. Answer key questions and our AI will create your case study.
            </p>
            <div className="space-y-2 text-xs text-gray-500 mb-6">
              <div className="flex items-center justify-center">
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">✓ Recommended</span>
              </div>
              <p>• 15-20 minutes to complete</p>
              <p>• Auto-saves as you go</p>
              <p>• AI enhances your responses</p>
            </div>
          </div>
          <a
            href="/questionnaire"
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium inline-block text-center"
          >
            Start Questionnaire
          </a>
        </div>

        {/* Alternative Path: File Upload */}
        <div className="bg-white bg-opacity-95 border-2 border-gray-200 rounded-lg p-6 flex flex-col shadow-lg">
          <div className="text-center flex-grow">
            <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload Document</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Have a completed questionnaire document or existing case study? Upload your file for instant processing.
            </p>
            <div className="space-y-2 text-xs text-gray-500 mb-6">
              <p>• Word (.docx) or text (.txt) questionnaires</p>
              <p>• Markdown (.md) case studies for revision</p>
              <p>• Files up to 10MB supported</p>
            </div>
          </div>
          <button
            onClick={() => fileUploadRef.current?.triggerFileSelect()}
            className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Upload File
          </button>
        </div>
        </div>
      )}


      {/* Check Status Banner */}
      {showCheckStatus && !currentJobId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-blue-900 mb-1">Resume Previous Session</h3>
              <p className="text-sm text-blue-700">
                It looks like you have an unfinished job. Would you like to check its status?
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCheckStatus(false)}
                className="px-3 py-1 text-sm border border-blue-300 text-blue-700 rounded hover:bg-blue-100 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={handleCheckStatus}
                className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Check Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Section - Only shown when file is selected but no case study generated yet */}
      {selectedFile && !caseStudy && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center text-green-600 mb-4">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">File Ready for Processing</h3>
              <p className="text-gray-600 text-sm mt-1">{selectedFile.name}</p>
              <p className="text-gray-500 text-xs">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => fileUploadRef.current?.triggerFileSelect()}
                disabled={apiLoading}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Choose Different File
              </button>
              <button
                onClick={handleGenerate}
                disabled={apiLoading || jobStatus === JOB_STATUSES.PROCESSING}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  apiLoading || jobStatus === JOB_STATUSES.PROCESSING
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {apiLoading || jobStatus === JOB_STATUSES.PROCESSING ? "Generating..." : "Generate Case Study"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden FileUpload component for file selection */}
      <div className="hidden">
        <FileUpload 
          ref={fileUploadRef}
          onFileSelect={handleFileSelect} 
          uploading={apiLoading} 
          selectedFile={selectedFile} 
        />
      </div>

      {/* Main Content */}
      <div className={`min-h-96 ${isProcessing ? "opacity-50 pointer-events-none" : ""}`}>
        {!caseStudy ? (
          <div id="upload-section" className="space-y-6">
            {/* Content moved above */}
          </div>
        ) : (
          <CaseStudyViewer
            caseStudy={activeVersion === "original" ? originalCaseStudy : caseStudy}
            onDownload={handleDownload}
            onFeedback={activeVersion === "current" ? handleFeedbackSubmit : null}
            onStartOver={handleStartOver}
            isProcessing={isProcessing}
            showFeedback={activeVersion === "current"}
            versionInfo={
              originalCaseStudy
                ? {
                    isRevised: activeVersion === "current",
                    hasOriginal: true,
                  }
                : null
            }
            hasVersions={!!originalCaseStudy}
            activeVersion={activeVersion}
            onVersionChange={setActiveVersion}
            originalCaseStudy={originalCaseStudy}
            revisionJobId={revisionJobId}
            baselineJobId={baselineJobId} // Pass baseline job ID for edit operations
            onMergeSections={handleMergeSections}
          />
        )}
      </div>

      {/* Processing Overlay - covers entire screen during processing */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-25 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">
                {getCurrentStatus() === JOB_STATUSES.UPLOADING
                  ? "Processing Your Request..."
                  : pollingJobType === "revision"
                    ? "Processing Your Revision..."
                    : pollingJobType === "merge"
                      ? "Creating Merged Case Study..."
                      : "Generating Case Study..."}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {getCurrentStatus() === JOB_STATUSES.UPLOADING
                  ? "Please wait while we process your file."
                  : pollingJobType === "revision"
                    ? "Your feedback is being applied to create a revised version."
                    : pollingJobType === "merge"
                      ? "Your selected sections are being combined into a new case study."
                      : "This may take a few minutes. Please do not refresh the page."}
              </p>
              {progress > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CaseStudyGenerator
